import {
  Box,
  Button,
  ClickAwayListener,
  IconButton,
  Paper,
  Popper,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { DynamicIcon } from 'src/components';

interface CorrectionPopupProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onSubmit: (correction: string) => void;
  onClose: () => void;
}

export const CorrectionPopup = ({
  anchorEl,
  open,
  onSubmit,
  onClose,
}: CorrectionPopupProps) => {
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      // Give time for the Popper to open before focusing
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 0);

      return () => clearTimeout(timer);
    }

    return;
  }, [open]);

  const handleClose = useCallback(() => {
    onClose();
    setInputValue('');
  }, [onClose]);

  const handleFormSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      onSubmit(inputValue);
      handleClose();
      setInputValue('');
    },
    [onSubmit, handleClose, inputValue],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        onSubmit(inputValue);
        handleClose();
        setInputValue('');
      }
    },
    [onSubmit, handleClose, inputValue],
  );

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="top-start"
      sx={{ zIndex: (theme) => theme.zIndex.modal }}
    >
      <ClickAwayListener onClickAway={handleClose}>
        <Paper elevation={3} sx={{ p: 1.5, minWidth: 250 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="caption" fontSize="0.85rem">
              Refine AI suggestion
            </Typography>
            <IconButton size="small" onClick={handleClose}>
              <DynamicIcon name="close" fontSize="small" />
            </IconButton>
          </Box>
          <Box
            component="form"
            onSubmit={handleFormSubmit}
            display="flex"
            flexDirection="column"
            gap={2}
          >
            <TextField
              placeholder="Enter instructions to refine the text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              size="small"
              fullWidth
              multiline
              minRows={3}
              inputRef={inputRef}
              onKeyDown={handleKeyDown}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: (theme) => theme.palette.custom.OV.sys.tertiary,
                  },
                },
                '& .MuiInputBase-root textarea': {
                  resize: 'both',
                  overflow: 'auto',
                },
              }}
            />

            <Button
              type="submit"
              variant="text"
              sx={{
                width: '60px',
                alignSelf: 'end',
                color: (theme) => theme.palette.custom.OV.sys.tertiary,
              }}
            >
              Apply
            </Button>
          </Box>
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
};
